import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { FiX, FiShield, FiCheckCircle } from 'react-icons/fi';
import { Button } from '@/components/common';

interface SincerityDeclarationModalProps {
  isOpen: boolean;
  candidateName?: string;
  organizationName?: string;
  onValidate: () => void;
  onCancel: () => void;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${theme.spacing.lg};
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${theme.shadows.xl};
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  padding: ${theme.spacing.xl};
  border-bottom: 1px solid ${theme.colors.surface};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.md};
  flex: 1;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.primary}15;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 24px;
    color: ${theme.colors.primary};
  }
`;

const TitleSection = styled.div`
  flex: 1;
`;

const Title = styled.h2`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${theme.spacing.sm};
  color: ${theme.colors.text}60;
  transition: all ${theme.transitions.fast};
  border-radius: ${theme.borderRadius.md};

  &:hover {
    background: ${theme.colors.surface};
    color: ${theme.colors.text};
  }

  svg {
    font-size: 20px;
  }
`;

const ModalBody = styled.div`
  padding: ${theme.spacing.xl};
`;

const DeclarationBox = styled.div`
  background: ${theme.colors.surface}50;
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
`;

const DeclarationText = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  line-height: 1.7;
  margin: 0 0 ${theme.spacing.md} 0;

  strong {
    color: ${theme.colors.primary};
    font-weight: ${theme.typography.weights.semibold};
  }
`;

const HighlightBox = styled.div`
  background: ${theme.colors.primary}10;
  border-left: 3px solid ${theme.colors.primary};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  margin: ${theme.spacing.lg} 0;
`;

const HighlightText = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  margin: 0;
  font-weight: ${theme.typography.weights.medium};
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.sm};

  svg {
    color: ${theme.colors.primary};
    font-size: 18px;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const ConsentList = styled.ul`
  margin: 0;
  padding: 0 0 0 ${theme.spacing.xl};
  list-style: none;

  li {
    font-size: ${theme.typography.sizes.sm};
    color: ${theme.colors.text}90;
    line-height: 1.6;
    margin-bottom: ${theme.spacing.sm};
    position: relative;

    &:before {
      content: "✓";
      position: absolute;
      left: -${theme.spacing.xl};
      color: ${theme.colors.success};
      font-weight: ${theme.typography.weights.bold};
    }
  }
`;

const WarningBox = styled.div`
  background: ${theme.colors.warning}10;
  border-left: 3px solid ${theme.colors.warning};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  margin-top: ${theme.spacing.lg};
`;

const WarningText = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  margin: 0;
  font-weight: ${theme.typography.weights.medium};
`;

const ModalFooter = styled.div`
  padding: ${theme.spacing.xl};
  border-top: 1px solid ${theme.colors.surface};
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
  position: sticky;
  bottom: 0;
  background: white;

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column-reverse;
  }
`;

export const SincerityDeclarationModal: React.FC<SincerityDeclarationModalProps> = ({
  isOpen,
  candidateName = '[Nom du Candidat Locataire]',
  organizationName = '[Nom de la Société de Gestion ou du Propriétaire]',
  onValidate,
  onCancel
}) => {
  return (
    <Overlay $isOpen={isOpen}>
      <ModalContainer>
        <ModalHeader>
          <HeaderContent>
            <IconWrapper>
              <FiShield />
            </IconWrapper>
            <TitleSection>
              <Title>Déclaration de Sincérité et Autorisation de Vérification</Title>
              <Subtitle>Dernière étape avant soumission</Subtitle>
            </TitleSection>
          </HeaderContent>
          <CloseButton onClick={onCancel}>
            <FiX />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <DeclarationBox>
            <DeclarationText>
              Je soussigné(e), <strong>{candidateName}</strong>, atteste par la présente que toutes les informations
              que j'ai fournies dans ce formulaire de candidature sont véridiques, complètes et exactes.
            </DeclarationText>

            <DeclarationText>
              Je comprends que l'exactitude de ces informations est cruciale pour l'évaluation de ma candidature
              en tant que locataire. Je suis conscient(e) que des informations incorrectes ou trompeuses pourraient
              entraîner le rejet de ma candidature.
            </DeclarationText>
          </DeclarationBox>

          <HighlightBox>
            <HighlightText>
              <FiCheckCircle />
              <span>
                Je consens à ce que <strong>{organizationName}</strong> et ses représentants aient le droit de
                vérifier les informations que j'ai fournies, notamment en examinant :
              </span>
            </HighlightText>
          </HighlightBox>

          <ConsentList>
            <li>Mon historique bancaire et ma situation financière</li>
            <li>Mes antécédents de location et références de propriétaires précédents</li>
            <li>Mon historique d'emploi et contacts avec mes employeurs actuels ou précédents</li>
            <li>Mes références personnelles et autres tiers pertinents</li>
          </ConsentList>

          <DeclarationText style={{ marginTop: theme.spacing.lg }}>
            Je reconnais que ma candidature peut être acceptée ou rejetée en fonction des résultats de ces
            vérifications des antécédents. Je consens à ce que ces vérifications soient menées dans le but de
            prendre une décision concernant ma candidature en tant que locataire.
          </DeclarationText>

          <WarningBox>
            <WarningText>
              En validant cette demande, j'affirme avoir lu, compris et accepté les termes et conditions spécifiques
              liés à la location et à la propriété gérée par {organizationName}.
            </WarningText>
          </WarningBox>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            style={{ minWidth: '120px' }}
          >
            J'annule
          </Button>
          <Button
            variant="primary"
            onClick={onValidate}
            style={{ minWidth: '120px' }}
          >
            Je valide
          </Button>
        </ModalFooter>
      </ModalContainer>
    </Overlay>
  );
};
